/**
 * Job endpoints (T025). Reads (list/get/logs/frames) replace the old Electric
 * sync path — they are polled by the Phase 2 hooks (T027). All use the default
 * refresh-on-401 retry.
 */
import { api } from '../http'
import {
  JobSchema,
  JobsResponseSchema,
  JobMutationResponseSchema,
  JobLogsResponseSchema,
  FramesResponseSchema,
  JobAttemptsResponseSchema,
} from '../schemas'
import { z } from 'zod'

const TopazVersionsResponseSchema = z.object({
  versions: z.array(z.string()),
})

/** `all` (admin-only, enforced server-side) lists every user's jobs. */
export const listJobs = (limit = 100, all = false) =>
  api.get(`/jobs?limit=${limit}${all ? '&all=true' : ''}`, JobsResponseSchema)

export const getJobAttempts = (id: string) =>
  api.get(`/jobs/${id}/attempts`, JobAttemptsResponseSchema)

export const getJob = (id: string) => api.get(`/jobs/${id}`, JobSchema)

/** body = SubmitJobReq { mode_params, name? } — shape validated server-side. */
export const submitJob = (body: unknown) =>
  api.post('/jobs', body, JobMutationResponseSchema)

export const submitTurntableFrameJob = (body: unknown) =>
  api.post('/turntable-frame-jobs', body, JobMutationResponseSchema)

export const listTopazVersions = () =>
  api.get('/jobs/topaz-versions', TopazVersionsResponseSchema)

export const cancelJob = (id: string) =>
  api.post(`/jobs/${id}/cancel`, undefined, JobMutationResponseSchema)

/** Create a new job with the same parameters as `id` (rerun of a finished job). */
export const rerunJob = (id: string) =>
  api.post(`/jobs/${id}/rerun`, undefined, JobMutationResponseSchema)

export const getJobLogs = (id: string, limit?: number) =>
  api.get(
    `/jobs/${id}/logs${limit !== undefined ? `?limit=${limit}` : ''}`,
    JobLogsResponseSchema,
  )

export const getFrames = (id: string, cursor?: string, limit = 50) => {
  const params = new URLSearchParams({ limit: String(limit) })
  if (cursor) params.set('cursor', cursor)
  return api.get(
    `/jobs/${id}/frames?${params.toString()}`,
    FramesResponseSchema,
  )
}
